import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { readPresentation } from "@/office/powerpoint/presentation";
import { MAKRO_MASTER } from "../makros";
import { nichtPruefbar } from "../schnittstelle";

export const lektionKopfFusszeileMaster: Lesson = {
  id: "kopf-fusszeile",
  title: "Fußzeilen, Notizen- und Handzettelmaster",
  summary: "Fußzeile, Datum und die Master für alles, was gedruckt wird.",
  durationMinutes: 10,
  learningGoals: [
    "Fußzeile und Datum über „Kopf- und Fußzeile“ einblenden",
    "Notizen- und Handzettelmaster für die Druckfassungen gestalten",
    "Führungslinien zum Ausrichten nutzen",
  ],
  steps: [
    {
      id: "fusszeile",
      title: "Fußzeile und Datum",
      explanation: [
        "„Einfügen“ → „Kopf- und Fußzeile“ blendet die Platzhalter ein, die der Folienmaster " +
          "für Datum, Foliennummer und Fußzeile vorsieht – an der Position, die der Master " +
          "festlegt. Getippte Textfelder am unteren Rand sind die Behelfslösung; sie sitzen " +
          "auf jeder Folie ein wenig anders.",
        "Das Häkchen „Auf Titelfolie nicht anzeigen“ hält die Titelfolie frei, und „Für alle " +
          "übernehmen“ wendet die Einstellung auf alle Folien an. Beim Datum lässt sich " +
          "zwischen „Automatisch aktualisieren“ und einem festen Datum wählen – für " +
          "Präsentationen, die weitergegeben werden, ist das feste Datum meist ehrlicher.",
      ],
      keyPoints: [
        "Einfügen → Kopf- und Fußzeile → Datum, Foliennummer, Fußzeile.",
        "„Für alle übernehmen“, Titelfolie ausnehmen.",
        "Festes Datum für Präsentationen, die weitergegeben werden.",
      ],
      task: {
        instruction: "Blende auf allen Inhaltsfolien eine Fußzeile mit Text und das Datum ein.",
        hint: "Einfügen → Kopf- und Fußzeile → Datum und Uhrzeit, Fußzeile mit Text → Für alle übernehmen.",
        check: async () => {
          const snapshot = await readPresentation();
          if (!snapshot.features.placeholders) {
            return nichtPruefbar("Die Platzhalterart einer Form", "1.8", "Sieh selbst nach, ob unten auf den Folien Fußzeile und Datum stehen.");
          }
          if (snapshot.slides.length < 2) {
            return fail("Die Präsentation braucht mindestens zwei Folien.", "Lege zuerst ein paar Inhaltsfolien an.");
          }
          const inhalt = snapshot.slides.slice(1);
          const mitFusszeile = inhalt.filter((folie) =>
            folie.shapes.some((form) => form.placeholder?.type === "Footer" && (form.text ?? "").trim().length > 0),
          );
          const mitDatum = inhalt.filter((folie) => folie.shapes.some((form) => form.placeholder?.type === "Date"));
          return fromCriteria(
            [
              criterion(`Fußzeile mit Text (${mitFusszeile.length} von ${inhalt.length} Inhaltsfolien)`, mitFusszeile.length === inhalt.length),
              criterion(`Datum (${mitDatum.length} von ${inhalt.length} Inhaltsfolien)`, mitDatum.length === inhalt.length),
            ],
            {
              success: "Fußzeile und Datum sitzen auf jeder Folie an derselben Stelle – vom Master vorgegeben.",
              failure: "Fußzeile oder Datum fehlen noch auf einigen Folien.",
              hint: "Ein getipptes Textfeld zählt nicht. „Für alle übernehmen“ statt „Übernehmen“ wählen.",
            },
          );
        },
      },
    },
    {
      id: "notizen-handzettel",
      title: "Notizen- und Handzettelmaster",
      explanation: [
        "Auch die Druckfassungen haben Master: „Ansicht“ → „Notizenmaster“ gestaltet die " +
          "Notizenseiten, „Ansicht“ → „Handzettelmaster“ die Handzettel mit mehreren Folien " +
          "pro Seite. Dort gehören etwa Logo, Titel der Veranstaltung und Seitenzahl hin.",
        "Kopf- und Fußzeilen für beide stehen im Dialog „Kopf- und Fußzeile“ auf der " +
          "Registerkarte „Notizen und Handzettel“ – dort gibt es anders als bei den Folien " +
          "auch eine Kopfzeile.",
      ],
      keyPoints: [
        "Ansicht → Notizenmaster / Handzettelmaster.",
        "Kopf- und Fußzeile → Registerkarte „Notizen und Handzettel“.",
      ],
      task: {
        instruction: "Gib Notizenseiten und Handzetteln eine Fußzeile mit Text, etwa dem Titel der Veranstaltung.",
        hint: "Einfügen → Kopf- und Fußzeile → Registerkarte „Notizen und Handzettel“ → Fußzeile → Für alle übernehmen.",
        macro: MAKRO_MASTER,
        check: () =>
          pruefeMitMakro(MAKRO_MASTER, (bericht) => {
            const notizen = bericht.janein("notizenFusszeile") === true && (bericht.text("notizenFusszeileText") ?? "").trim().length > 0;
            const handzettel = bericht.janein("handzettelFusszeile") === true && (bericht.text("handzettelFusszeileText") ?? "").trim().length > 0;
            return fromCriteria(
              [
                criterion("Notizenseiten haben eine Fußzeile mit Text", notizen),
                criterion("Handzettel haben eine Fußzeile mit Text", handzettel),
              ],
              {
                success: "Auch die Druckfassungen tragen jetzt ihre Kennzeichnung.",
                failure: "Die Fußzeile für Notizen und Handzettel fehlt noch.",
                hint: "Fehlt eine der beiden, einmal Ansicht → Handzettelmaster öffnen und schließen, dann erneut prüfen.",
              },
            );
          }),
      },
    },
    {
      id: "fuehrungslinien",
      title: "Führungslinien",
      explanation: [
        "„Ansicht“ → „Führungslinien“ blendet Hilfslinien ein, an denen Formen einrasten. " +
          "Rechtsklick auf die Folie → „Raster und Führungslinien“ → „Vertikale " +
          "Führungslinie hinzufügen“ setzt weitere, etwa für einen einheitlichen Rand. " +
          "Mit gedrückter Strg-Taste lässt sich eine Linie ziehen und kopieren.",
        "Führungslinien, die im Folienmaster gesetzt werden, erscheinen auf allen Folien und " +
          "lassen sich dort nicht versehentlich verschieben – ideal für Vorlagen.",
      ],
      keyPoints: [
        "Ansicht → Führungslinien.",
        "Rechtsklick → Raster und Führungslinien → Führungslinie hinzufügen.",
        "Im Folienmaster gesetzt, gelten sie für alle Folien.",
      ],
      task: {
        instruction: "Füge mindestens eine eigene Führungslinie hinzu.",
        hint: "Rechtsklick auf eine leere Stelle der Folie → Raster und Führungslinien → Vertikale Führungslinie hinzufügen.",
        macro: MAKRO_MASTER,
        check: () =>
          pruefeMitMakro(MAKRO_MASTER, (bericht) =>
            fromCriteria([criterion(`Eigene Führungslinien: ${bericht.zahl("fuehrungslinien") ?? 0}`, (bericht.zahl("fuehrungslinien") ?? 0) > 0)], {
              success: "Die Linie hilft beim Ausrichten – Formen rasten daran ein.",
              failure: "Es gibt noch keine eigene Führungslinie.",
              hint: "Die eingeblendeten Standardlinien zählen nicht; es muss eine hinzugefügte sein.",
            }),
          ),
      },
    },
  ],
};
